import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ChatSession } from "@/types";

function formatSessionLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function SessionPicker({
  sessions,
  activeSessionId,
  isDraftActive = false,
  onSelect,
  onNewChat,
  allowNewChat = true,
}: {
  sessions: ChatSession[];
  activeSessionId: string | null;
  /** True when composing a new chat that is not saved until the first message. */
  isDraftActive?: boolean;
  onSelect: (sessionId: string) => void;
  onNewChat?: () => void;
  allowNewChat?: boolean;
}) {
  const sorted = [...sessions].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  return (
    <div className="flex w-full min-w-0 shrink-0 items-center gap-2 border-b border-slate-100 bg-white px-3 py-2.5 sm:px-4">
      {allowNewChat && onNewChat && (
        <button
          type="button"
          onClick={onNewChat}
          aria-selected={isDraftActive}
          className={cn(
            "flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition",
            isDraftActive
              ? "border-green/50 bg-green text-navy shadow-sm"
              : "border-dashed border-slate-300 bg-white text-navy hover:border-green/50 hover:bg-green/5",
          )}
        >
          <Plus className="size-4" />
        </button>
      )}

      <div
        data-allow-scroll
        role="tablist"
        aria-label="Chat sessions"
        className="flex min-w-0 flex-1 touch-pan-x gap-2 overflow-x-auto overscroll-x-contain scroll-smooth pb-0.5 [-webkit-overflow-scrolling:touch]"
      >
        {sorted.map((session) => {
          const active = session.id === activeSessionId;
          return (
            <button
              key={session.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelect(session.id)}
              className={cn(
                "flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition",
                active
                  ? "bg-green text-navy shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200",
              )}
            >
              {formatSessionLabel(session.created_at)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
